import { type RefObject, useCallback, useEffect, useRef } from 'react';

interface UseStickyBottomOptions {
  scrollRef: RefObject<HTMLElement | null>;
  /** 进入钉底窗口的开关。false 时不挂任何监听 / RAF */
  active: boolean;
  /** 用户主动滚动后的钉底策略：
   *  - 'permanent'：用户一旦交互就停止本轮钉底（ShareChat 自动连播用）
   *  - 'follow-flag'：每帧读 shouldPin() 决定是否贴底，用户上滑时让位、回到底部又继续（Chat 流式打字用） */
  userInteractionPolicy: 'permanent' | 'follow-flag';
  /** follow-flag 模式下读取的开关 */
  shouldPin?: () => boolean;
  /** 闲置兜底：连续 idleMs 没有调用 markActivity 就触发 onIdle，仅在 active=true 内部计时 */
  idleMs?: number;
  onIdle?: () => void;
}

interface UseStickyBottomReturn {
  /** 上报"内容仍在增长"信号，用于推迟 idleMs 触发 */
  markActivity: () => void;
}

const PIN_TOLERANCE_PX = 1;

export const useStickyBottom = ({
  scrollRef,
  active,
  userInteractionPolicy,
  shouldPin,
  idleMs,
  onIdle,
}: UseStickyBottomOptions): UseStickyBottomReturn => {
  const lastActivityAtRef = useRef(0);
  const shouldPinRef = useRef(shouldPin);
  shouldPinRef.current = shouldPin;
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!active) return;
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    lastActivityAtRef.current = performance.now();
    let userInterrupted = false;
    const onUserInteract = () => {
      userInterrupted = true;
    };
    const interactOpts: AddEventListenerOptions = { passive: true, capture: true };
    if (userInteractionPolicy === 'permanent') {
      scrollEl.addEventListener('wheel', onUserInteract, interactOpts);
      scrollEl.addEventListener('touchstart', onUserInteract, interactOpts);
      scrollEl.addEventListener('keydown', onUserInteract, interactOpts);
    }

    let rafId: number | null = null;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      if (userInteractionPolicy === 'permanent' && userInterrupted) {
        rafId = null;
        return;
      }
      const node = scrollRef.current;
      if (!node) {
        rafId = null;
        return;
      }
      const allowPin = userInteractionPolicy === 'permanent' ? !userInterrupted : (shouldPinRef.current?.() ?? true);
      if (allowPin) {
        const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);
        if (maxScroll - node.scrollTop > PIN_TOLERANCE_PX) {
          node.scrollTop = maxScroll;
        }
      }
      if (idleMs !== undefined && performance.now() - lastActivityAtRef.current >= idleMs) {
        rafId = null;
        onIdleRef.current?.();
        return;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (userInteractionPolicy === 'permanent') {
        scrollEl.removeEventListener('wheel', onUserInteract, interactOpts);
        scrollEl.removeEventListener('touchstart', onUserInteract, interactOpts);
        scrollEl.removeEventListener('keydown', onUserInteract, interactOpts);
      }
    };
  }, [active, idleMs, scrollRef, userInteractionPolicy]);

  return {
    markActivity: useCallback(() => {
      lastActivityAtRef.current = performance.now();
    }, []),
  };
};
